<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AssistantAPI from 'dashboard/api/assistant';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AssistantPageLayout from '../components/AssistantPageLayout.vue';
import AssistantSection from '../components/AssistantSection.vue';
import AssistantLoadError from '../components/AssistantLoadError.vue';
import { usePersistentRef } from '../composables/useAssistantCache';

const { t } = useI18n();
const tools = usePersistentRef('tools', []);
// Tools list may be legitimately empty, so a flag (not emptiness) marks
// "loaded at least once" — skeleton only before the first load.
const toolsLoaded = usePersistentRef('tools-loaded', false);
const isFetching = ref(!toolsLoaded.value);
const loadError = ref(false);
const route = useRoute();
const googleStatus = usePersistentRef('google-status', {
  connected: false,
  email: null,
});

const fetchGoogleStatus = async () => {
  try {
    const { data } = await AssistantAPI.getGoogleStatus();
    googleStatus.value = data;
  } catch (error) {
    googleStatus.value = { connected: false, email: null };
  }
};

const signInWithGoogle = async () => {
  try {
    const { data } = await AssistantAPI.getGoogleAuthorizeUrl();
    window.location.href = data.url;
  } catch (error) {
    useAlert(t('ASSISTANT.TOOLS.GOOGLE.ERROR_TOAST'));
  }
};

const disconnectGoogle = async () => {
  try {
    await AssistantAPI.disconnectGoogle();
    googleStatus.value = { connected: false, email: null };
  } catch (error) {
    useAlert(t('ASSISTANT.TOOLS.SAVE_ERROR'));
  }
};

const fetchAll = async () => {
  loadError.value = false;
  isFetching.value = !toolsLoaded.value;
  try {
    const { data } = await AssistantAPI.getTools();
    tools.value = data;
    toolsLoaded.value = true;
  } catch (error) {
    loadError.value = true;
  } finally {
    isFetching.value = false;
  }
  await fetchGoogleStatus();
};

const HTTP_METHODS = ['GET', 'POST'];
const PARAM_TYPES = ['string', 'number', 'boolean'];

// --- Shared tool list helpers ---
const sheetsTools = () =>
  tools.value.filter(x => x.tool_type === 'google_sheets');
const httpTools = () => tools.value.filter(x => x.tool_type === 'http');

const deleteTool = async tool => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('ASSISTANT.TOOLS.COMMON.DELETE_CONFIRM'))) return;
  try {
    await AssistantAPI.deleteTool(tool.id);
    tools.value = tools.value.filter(x => x.id !== tool.id);
    useAlert(t('ASSISTANT.TOOLS.DELETED'));
  } catch (error) {
    useAlert(t('ASSISTANT.TOOLS.DELETE_ERROR'));
  }
};

const toggleTool = async tool => {
  try {
    const { data } = await AssistantAPI.updateTool(tool.id, {
      enabled: !tool.enabled,
    });
    Object.assign(tool, data);
  } catch (error) {
    useAlert(t('ASSISTANT.TOOLS.SAVE_ERROR'));
  }
};

// --- Google Sheets create form ---
const showSheetForm = ref(false);
const emptySheetForm = () => ({
  title: '',
  description: '',
  sheet_url: '',
  allow_append: false,
});
const sheetForm = ref(emptySheetForm());
const isSavingSheet = ref(false);
const createSheetTool = async () => {
  isSavingSheet.value = true;
  try {
    const { data } = await AssistantAPI.createTool({
      tool_type: 'google_sheets',
      title: sheetForm.value.title,
      description: sheetForm.value.description,
      config: {
        sheet_url: sheetForm.value.sheet_url,
        allow_append: sheetForm.value.allow_append,
      },
    });
    tools.value.push(data);
    sheetForm.value = emptySheetForm();
    showSheetForm.value = false;
    useAlert(t('ASSISTANT.TOOLS.SHEETS.CREATED'));
  } catch (error) {
    const detail = error?.response?.data?.details?.detail;
    let msg = t('ASSISTANT.TOOLS.SAVE_ERROR');
    if (detail === 'google_not_connected') {
      msg = t('ASSISTANT.TOOLS.GOOGLE.CONNECT_FIRST');
    } else if (detail === 'cannot_open_sheet') {
      msg = t('ASSISTANT.TOOLS.SHEETS.NOT_SHARED');
    }
    useAlert(msg);
  } finally {
    isSavingSheet.value = false;
  }
};

// --- HTTP tool create form ---
const showHttpForm = ref(false);
const emptyHttpForm = () => ({
  title: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  auth_type: 'none',
  auth_config: {
    token: '',
    username: '',
    password: '',
    header_name: '',
    key: '',
  },
  param_schema: [],
});
const httpForm = ref(emptyHttpForm());
const isSavingHttp = ref(false);
const addParam = () => {
  httpForm.value.param_schema.push({
    name: '',
    type: 'string',
    description: '',
    required: false,
  });
};
const removeParam = index => {
  httpForm.value.param_schema.splice(index, 1);
};
const createHttpTool = async () => {
  isSavingHttp.value = true;
  try {
    const form = httpForm.value;
    const { data } = await AssistantAPI.createTool({
      tool_type: 'http',
      title: form.title,
      description: form.description,
      config: {
        endpoint_url: form.endpoint_url,
        http_method: form.http_method,
        auth_type: form.auth_type,
        auth_config: form.auth_config,
        param_schema: form.param_schema,
      },
    });
    tools.value.push(data);
    httpForm.value = emptyHttpForm();
    showHttpForm.value = false;
    useAlert(t('ASSISTANT.TOOLS.HTTP.CREATED'));
  } catch (error) {
    useAlert(t('ASSISTANT.TOOLS.SAVE_ERROR'));
  } finally {
    isSavingHttp.value = false;
  }
};

onMounted(() => {
  fetchAll();
  if (route.query.google === 'connected') {
    useAlert(t('ASSISTANT.TOOLS.GOOGLE.CONNECTED_TOAST'));
  } else if (route.query.google === 'error') {
    useAlert(t('ASSISTANT.TOOLS.GOOGLE.ERROR_TOAST'));
  }
});
</script>

<template>
  <AssistantPageLayout
    :header-title="t('ASSISTANT.TOOLS.TITLE')"
    :description="t('ASSISTANT.TOOLS.DESCRIPTION')"
    :is-fetching="isFetching"
  >
    <AssistantLoadError
      v-if="loadError"
      class="mb-5"
      :message="t('ASSISTANT.TOOLS.LOAD_ERROR')"
      @retry="fetchAll"
    />
    <div v-if="toolsLoaded" class="flex min-w-0 flex-col gap-5">
      <AssistantSection
        :heading="t('ASSISTANT.TOOLS.SECTIONS.SHEETS.TITLE')"
        :description="t('ASSISTANT.TOOLS.SECTIONS.SHEETS.DESCRIPTION')"
        :count="sheetsTools().length"
        icon="i-lucide-table-2"
      >
        <template #actions>
          <Button
            type="button"
            size="sm"
            icon="i-lucide-plus"
            class="min-h-11"
            :label="t('ASSISTANT.TOOLS.SHEETS.ADD')"
            :disabled="!googleStatus.connected"
            :aria-expanded="showSheetForm"
            :aria-controls="showSheetForm ? 'sheet-tool-form' : undefined"
            @click="showSheetForm = !showSheetForm"
          />
        </template>
        <div
          class="flex min-w-0 flex-col gap-3 border-b border-n-weak p-4 sm:px-5"
        >
          <p class="text-sm text-n-slate-11">
            {{ t('ASSISTANT.TOOLS.GOOGLE.INTRO') }}
          </p>
          <div
            v-if="googleStatus.connected"
            class="flex min-w-0 flex-col items-start justify-between gap-3 rounded-md border border-n-teal-7 bg-n-teal-3 p-3 sm:flex-row sm:items-center"
          >
            <span
              class="flex min-w-0 items-start gap-2 text-sm font-medium text-n-teal-11 sm:flex-1"
            >
              <Icon
                icon="i-lucide-circle-check"
                class="mt-0.5 size-4 shrink-0"
              />
              <span class="min-w-0 break-words [overflow-wrap:anywhere]">
                {{
                  googleStatus.email
                    ? t('ASSISTANT.TOOLS.GOOGLE.CONNECTED_AS', {
                        email: googleStatus.email,
                      })
                    : t('ASSISTANT.TOOLS.GOOGLE.CONNECTED')
                }}
              </span>
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              color="ruby"
              :label="t('ASSISTANT.TOOLS.GOOGLE.DISCONNECT')"
              @click="disconnectGoogle"
            />
          </div>
          <Button
            v-else
            type="button"
            size="sm"
            :label="t('ASSISTANT.TOOLS.GOOGLE.SIGN_IN')"
            @click="signInWithGoogle"
          />
        </div>

        <div
          v-if="showSheetForm"
          id="sheet-tool-form"
          class="m-4 flex min-w-0 flex-col gap-4 rounded-md border border-n-weak bg-n-alpha-1 p-4 sm:m-5"
        >
          <Input
            v-model="sheetForm.title"
            :label="t('ASSISTANT.TOOLS.SHEETS.NAME')"
            :placeholder="t('ASSISTANT.TOOLS.SHEETS.NAME_PLACEHOLDER')"
          />
          <TextArea
            id="sheet-tool-purpose"
            v-model="sheetForm.description"
            :label="t('ASSISTANT.TOOLS.SHEETS.PURPOSE')"
            :placeholder="t('ASSISTANT.TOOLS.SHEETS.PURPOSE_PLACEHOLDER')"
            auto-height
          />
          <Input
            v-model="sheetForm.sheet_url"
            type="url"
            :label="t('ASSISTANT.TOOLS.SHEETS.URL')"
            :placeholder="t('ASSISTANT.TOOLS.SHEETS.URL_PLACEHOLDER')"
          />
          <div class="flex items-center justify-between w-full gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('ASSISTANT.TOOLS.SHEETS.ALLOW_APPEND') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('ASSISTANT.TOOLS.SHEETS.ALLOW_APPEND_DESCRIPTION') }}
              </span>
            </div>
            <Switch
              v-model="sheetForm.allow_append"
              :aria-label="t('ASSISTANT.TOOLS.SHEETS.ALLOW_APPEND')"
            />
          </div>
          <div class="flex gap-2">
            <Button
              type="button"
              :label="t('ASSISTANT.TOOLS.COMMON.SAVE')"
              :is-loading="isSavingSheet"
              :disabled="
                isSavingSheet || !sheetForm.title || !sheetForm.sheet_url
              "
              @click="createSheetTool"
            />
            <Button
              type="button"
              variant="ghost"
              :label="t('ASSISTANT.TOOLS.COMMON.CANCEL')"
              @click="showSheetForm = false"
            />
          </div>
        </div>

        <div v-if="sheetsTools().length" class="divide-y divide-n-weak">
          <article
            v-for="tool in sheetsTools()"
            :key="tool.id"
            class="flex min-w-0 flex-col gap-4 p-4 sm:flex-row sm:items-center sm:px-5"
          >
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <span class="break-words text-sm font-medium text-n-slate-12">
                {{ tool.title }}
              </span>
              <span class="break-all text-xs leading-5 text-n-slate-11">
                <bdi>{{ tool.config.sheet_url }}</bdi>
                <span v-if="tool.config.allow_append" class="ms-2 text-n-brand">
                  {{ t('ASSISTANT.TOOLS.SHEETS.CAN_WRITE_BADGE') }}
                </span>
              </span>
              <div
                v-if="tool.config.columns && tool.config.columns.length"
                class="flex flex-wrap items-center gap-1 mt-1"
              >
                <span class="text-xs text-n-slate-10">
                  {{ t('ASSISTANT.TOOLS.SHEETS.COLUMNS_FOUND') }}
                </span>
                <span
                  v-for="(col, idx) in tool.config.columns"
                  :key="idx"
                  class="max-w-full break-words rounded px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-11 [overflow-wrap:anywhere]"
                >
                  {{ col }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <Switch
                :model-value="tool.enabled"
                :aria-label="`${t('ASSISTANT.TOOLS.COMMON.ENABLED')}: ${tool.title}`"
                @update:model-value="toggleTool(tool)"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                :label="t('ASSISTANT.TOOLS.COMMON.DELETE')"
                :aria-label="`${t('ASSISTANT.TOOLS.COMMON.DELETE')}: ${tool.title}`"
                class="min-h-11"
                @click="deleteTool(tool)"
              />
            </div>
          </article>
        </div>
      </AssistantSection>
      <AssistantSection
        :heading="t('ASSISTANT.TOOLS.SECTIONS.HTTP.TITLE')"
        :description="t('ASSISTANT.TOOLS.SECTIONS.HTTP.DESCRIPTION')"
        :count="httpTools().length"
        icon="i-lucide-plug"
      >
        <template #actions>
          <Button
            type="button"
            size="sm"
            icon="i-lucide-plus"
            class="min-h-11"
            :label="t('ASSISTANT.TOOLS.HTTP.ADD')"
            :aria-expanded="showHttpForm"
            :aria-controls="showHttpForm ? 'http-tool-form' : undefined"
            @click="showHttpForm = !showHttpForm"
          />
        </template>
        <div
          v-if="showHttpForm"
          id="http-tool-form"
          class="m-4 flex min-w-0 flex-col gap-4 rounded-md border border-n-weak bg-n-alpha-1 p-4 sm:m-5"
        >
          <Input
            v-model="httpForm.title"
            :label="t('ASSISTANT.TOOLS.HTTP.NAME')"
            :placeholder="t('ASSISTANT.TOOLS.HTTP.NAME_PLACEHOLDER')"
          />
          <TextArea
            id="http-tool-purpose"
            v-model="httpForm.description"
            :label="t('ASSISTANT.TOOLS.HTTP.PURPOSE')"
            :placeholder="t('ASSISTANT.TOOLS.HTTP.PURPOSE_PLACEHOLDER')"
            auto-height
          />
          <Input
            v-model="httpForm.endpoint_url"
            type="url"
            :label="t('ASSISTANT.TOOLS.HTTP.URL')"
            placeholder="https://api.example.com/orders"
          />
          <div class="flex flex-col gap-1">
            <label
              for="http-tool-method"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ t('ASSISTANT.TOOLS.HTTP.METHOD') }}
            </label>
            <select
              id="http-tool-method"
              v-model="httpForm.http_method"
              class="min-h-11 w-full rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 ltr:pr-8 rtl:pl-8 focus:border-n-brand focus:ring-1 focus:ring-n-brand sm:max-w-xs"
            >
              <option
                v-for="method in HTTP_METHODS"
                :key="method"
                :value="method"
              >
                {{ method }}
              </option>
            </select>
            <p
              v-if="httpForm.http_method === 'POST'"
              class="mt-1 text-sm text-n-amber-11"
            >
              {{ t('ASSISTANT.TOOLS.HTTP.POST_WARNING') }}
            </p>
          </div>
          <div class="flex flex-col gap-1">
            <label
              for="http-tool-auth"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ t('ASSISTANT.TOOLS.HTTP.AUTH') }}
            </label>
            <select
              id="http-tool-auth"
              v-model="httpForm.auth_type"
              class="min-h-11 w-full rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 ltr:pr-8 rtl:pl-8 focus:border-n-brand focus:ring-1 focus:ring-n-brand sm:max-w-xs"
            >
              <option value="none">
                {{ t('ASSISTANT.TOOLS.HTTP.AUTH_NONE') }}
              </option>
              <option value="bearer">
                {{ t('ASSISTANT.TOOLS.HTTP.AUTH_BEARER') }}
              </option>
              <option value="basic">
                {{ t('ASSISTANT.TOOLS.HTTP.AUTH_BASIC') }}
              </option>
              <option value="api_key">
                {{ t('ASSISTANT.TOOLS.HTTP.AUTH_API_KEY') }}
              </option>
            </select>
          </div>
          <Input
            v-if="httpForm.auth_type === 'bearer'"
            v-model="httpForm.auth_config.token"
            type="password"
            :label="t('ASSISTANT.TOOLS.HTTP.TOKEN')"
          />
          <template v-if="httpForm.auth_type === 'basic'">
            <Input
              v-model="httpForm.auth_config.username"
              :label="t('ASSISTANT.TOOLS.HTTP.USERNAME')"
            />
            <Input
              v-model="httpForm.auth_config.password"
              type="password"
              :label="t('ASSISTANT.TOOLS.HTTP.PASSWORD')"
            />
          </template>
          <template v-if="httpForm.auth_type === 'api_key'">
            <Input
              v-model="httpForm.auth_config.header_name"
              :label="t('ASSISTANT.TOOLS.HTTP.HEADER_NAME')"
              placeholder="X-Api-Key"
            />
            <Input
              v-model="httpForm.auth_config.key"
              type="password"
              :label="t('ASSISTANT.TOOLS.HTTP.KEY')"
            />
          </template>

          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ASSISTANT.TOOLS.HTTP.PARAMS') }}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              :label="t('ASSISTANT.TOOLS.HTTP.PARAM_ADD')"
              @click="addParam"
            />
          </div>
          <div
            v-for="(param, index) in httpForm.param_schema"
            :key="index"
            class="grid min-w-0 grid-cols-1 items-end gap-3 rounded-md border border-n-weak bg-n-surface-1 p-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            <Input
              v-model="param.name"
              :label="t('ASSISTANT.TOOLS.HTTP.PARAM_NAME')"
              class="min-w-0"
            />
            <div class="flex min-w-0 flex-col gap-1 text-sm text-n-slate-12">
              <label :for="`http-param-type-${index}`">
                {{ t('ASSISTANT.TOOLS.HTTP.PARAM_TYPE') }}
              </label>
              <select
                :id="`http-param-type-${index}`"
                v-model="param.type"
                class="min-h-11 w-full rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 ltr:pr-8 rtl:pl-8 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option
                  v-for="ptype in PARAM_TYPES"
                  :key="ptype"
                  :value="ptype"
                >
                  {{ ptype }}
                </option>
              </select>
            </div>
            <Input
              v-model="param.description"
              :label="t('ASSISTANT.TOOLS.HTTP.PARAM_DESCRIPTION')"
              class="min-w-0"
            />
            <div class="flex min-h-11 items-center justify-between gap-3">
              <label class="flex items-center gap-2 text-sm text-n-slate-11">
                <input v-model="param.required" type="checkbox" />
                {{ t('ASSISTANT.TOOLS.HTTP.PARAM_REQUIRED') }}
              </label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                :label="t('ASSISTANT.TOOLS.COMMON.DELETE')"
                :aria-label="
                  t('ASSISTANT.TOOLS.HTTP.PARAM_DELETE', { number: index + 1 })
                "
                class="min-h-11"
                @click="removeParam(index)"
              />
            </div>
          </div>

          <div class="flex gap-2">
            <Button
              type="button"
              :label="t('ASSISTANT.TOOLS.COMMON.SAVE')"
              :is-loading="isSavingHttp"
              :disabled="
                isSavingHttp || !httpForm.title || !httpForm.endpoint_url
              "
              @click="createHttpTool"
            />
            <Button
              type="button"
              variant="ghost"
              :label="t('ASSISTANT.TOOLS.COMMON.CANCEL')"
              @click="showHttpForm = false"
            />
          </div>
        </div>

        <div v-if="httpTools().length" class="divide-y divide-n-weak">
          <article
            v-for="tool in httpTools()"
            :key="tool.id"
            class="flex min-w-0 flex-col gap-4 p-4 sm:flex-row sm:items-center sm:px-5"
          >
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <span class="break-words text-sm font-medium text-n-slate-12">
                {{ tool.title }}
              </span>
              <span class="break-all text-xs leading-5 text-n-slate-11">
                <bdi>{{
                  `${tool.config.http_method} ${tool.config.endpoint_url}`
                }}</bdi>
              </span>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <Switch
                :model-value="tool.enabled"
                :aria-label="`${t('ASSISTANT.TOOLS.COMMON.ENABLED')}: ${tool.title}`"
                @update:model-value="toggleTool(tool)"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                :label="t('ASSISTANT.TOOLS.COMMON.DELETE')"
                :aria-label="`${t('ASSISTANT.TOOLS.COMMON.DELETE')}: ${tool.title}`"
                class="min-h-11"
                @click="deleteTool(tool)"
              />
            </div>
          </article>
        </div>
      </AssistantSection>
    </div>
  </AssistantPageLayout>
</template>
